import { Injectable } from "@angular/core";
import { BaseApiService } from "../../shared/utils/base-api.service";
import { HttpClient, HttpContext, HttpParams } from "@angular/common/http";
import { MatSnackBar } from "@angular/material/snack-bar";
import { map, Observable } from "rxjs";
import { APIResponse } from "../../core/models/api.response.model";
import { DoctorProfile, TeamMember, TeamUsecase } from "../../core/models/users.models";
import { CACHE_TTL, CACHE_TTL_HIGH } from "../../../@fuse/services/cache/cache.interceptor";

@Injectable({
    providedIn: "root",
})
export class UsersAPIService extends BaseApiService {

    constructor(private _httpClient: HttpClient, matSnackBar: MatSnackBar) {
        super(matSnackBar);
    }

    getDoctorProfile(doctorID: string): Observable<DoctorProfile> {
        return this._httpClient
            .get<APIResponse<DoctorProfile>>(
                `${this.BASE_URL}/users/doctor_profile_by_id`,
                {
                    params: {
                        user_id: doctorID,
                    },
                }
            )
            .pipe(
                this.delayedRetry(),
                this.errorHandler,
                this.announceFailureResponse,
                map((value: APIResponse<DoctorProfile>) => value.data)
            );
    }

    saveDoctorProfile(doctorID: string, profile: Partial<DoctorProfile>): Observable<DoctorProfile> {
        return this._httpClient
            .post<APIResponse<DoctorProfile>>(
                `${this.BASE_URL}/users/doctor_profile_by_id`,
                {
                    user_id: doctorID,
                    ...profile
                }
            )
            .pipe(
                this.errorHandler,
                this.announceFailureResponse,
                map((value: APIResponse<DoctorProfile>) => value.data)
            );
    }

    uploadDoctorProfilePicture(doctorID: string, image: File): Observable<APIResponse<any>> {
        const formData = new FormData();
        formData.append('file', image, image.name);
        formData.append('user_id', doctorID);

        return this._httpClient
            .post<APIResponse<any>>(
                `${this.BASE_URL}/users/profile/photo/`,
                formData,
            )
            .pipe(
                this.errorHandler,
                this.announceFailureResponse,
            );
    }

    sendNotification(title: string, body: string, userIDs: string[]): Observable<boolean> {
        return this._httpClient
            .post<any>(`${this.BASE_URL}/users/send_app_notification`, {
                title: title,
                body: body,
                user_ids: userIDs,
            })
            .pipe(
                this.errorHandler,
                this.announceFailureResponse,
                map((response: APIResponse<any>) => {
                    return response.success;
                })
            );
    }


    sendActivityLog(userID: string, timestamp: string, additionalData: any): Observable<boolean> {
        return this._httpClient
            .post<any>(`${this.BASE_URL}/users/aceso_activity_log`, {
                user_id: userID,
                timestamp: timestamp,
                additional_data: additionalData,
            })
            .pipe(
                this.errorHandler,
                map((response: APIResponse<any>) => {
                    return response.success;
                })
            );
    }


    getNavBarAlertCount(navBarItems: string[]): Observable<Map<string, number>> {
        let navBarItemParam = navBarItems.join(',');
        return this._httpClient
            .get<APIResponse<any>>(
                `${this.BASE_URL}/careplan/get_navbar_items_alert_counts`,
                {
                    params: {
                        items: navBarItemParam,
                    }
                }
            )
            .pipe(
                this.delayedRetry(),
                this.errorHandler,
                this.announceFailureResponse,
                map((value: APIResponse<any>) => value.data)
            );
    }

    getTeamMembers(usecase: TeamUsecase, ownerID: string): Observable<TeamMember[]> {
        let params = new HttpParams({
            fromObject: {
                usecase: usecase,
            }
        });
        if (ownerID) {
            params = params.append('owner_id', ownerID);
        }
        return this._httpClient
            .get<APIResponse<any>>(
                `${this.BASE_URL}/users/internal_team_members`,
                {
                    context: new HttpContext().set(CACHE_TTL, CACHE_TTL_HIGH),
                    params: params
                }
            )
            .pipe(
                this.delayedRetry(),
                this.errorHandler,
                this.announceFailureResponse,
                map((value: APIResponse<any>) => value.data)
            );
    }

}
